import { yupResolver } from '@hookform/resolvers/yup';
import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  ProductSaleMutationInput,
  ProductSalesModel,
} from 'api-hooks/product/sales';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { FormState } from 'components/elements/form';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import SalesActionComponent from 'legacy-modules/sales/component/sales-action-component';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import CostField from './cost-field';
import {
  ProductSaleDetailFormType,
  ProductSaleFormSchema,
  ProductSaleFormType,
} from './form-type';
import InformationField from './information-field';
import ProductDetailField from './product-detail-field';
import SalesDetailField from './sales-detail-field';
import ProductSalesSelectInput from './select-input';
import { FormContent, FormDivider } from './styles';
import BottomSection from './summary-price/bottom-section';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  productSale?: ProductSalesModel;
  onSubmit: (
    input: ProductSaleMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ProductSalesModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: ProductSaleFormType;
}

export default function ProductSaleForm(props: Props) {
  const { productSale } = props;
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = useGetDefaultValues({
    productSale,
    defaultValues: props.defaultValues,
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(ProductSaleFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: ProductSaleFormType) => {
      const { tempTransactionAt, customerBillingOptions, ...rest } = values;
      try {
        const productSales = values.productSales.map((ps) => {
          return {
            ...ps,
            productVariantId: ps.productVariantId.id,
          };
        });

        const needApproval =
          values.transactionAt.getTime() !== tempTransactionAt.getTime();

        const result = {
          ...rest,
          needApproval,
          productSales,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const isContribute =
    productSale?.admin.id === employeeHook?.id ||
    productSale?.salesman?.id === employeeHook?.id ||
    productSale?.operator?.id === employeeHook?.id;

  const customViewName =
    productSale &&
    `${t('common:product_sales')} - ${productSale.invoiceNumber}`;

  const { getValues, setValue } = methods;

  //autofill
  const onSuccessCreateGuest = React.useCallback(
    (guest: GuestModel) => {
      const productSales: ProductSaleDetailFormType[] = getValues(
        'productSales',
      ).map((ps) => {
        return {
          ...ps,
        };
      });
      setValue('productSales', productSales);
    },
    [getValues, setValue],
  );

  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreateGuest,
  });

  const actionComponent = productSale && (
    <SalesActionComponent
      data={productSale}
      defaultValues={defaultValues}
      type="product-sales"
    />
  );

  const { can } = useAuthorization();
  const core = can(AuthorizationRules.SalesCore);

  const onRenderRightComponent = productSale && (isContribute || core) && (
    <>{actionComponent}</>
  );

  const onRelatedSalesAfterChange = React.useCallback(
    (value: ProductSalesModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!productSale}
      customViewName={customViewName}
      keyName={asPath}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <FormContent>
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={productSale}
              noSubmit
              noDelete
              title={
                productSale ? (
                  <TitleStatus
                    status={productSale.status}
                    title={customViewName ?? t('common:product_sales')}
                    isCompleted={productSale.isCompleted}
                    type="sale-default"
                  />
                ) : (
                  t('common:add_extra', { extra: t('common:product_sales') })
                )
              }
              noEdit
              onRenderRightComponent={
                editable ? createGuestComponent : onRenderRightComponent
              }
            />
          )}
        </FormState>
        {props.renderError}
        <div>
          {productSale &&
            (!!productSale.parentSale ||
              !!(productSale.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={productSale}
                navigation="ProductSalesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <ProductSalesSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedSalesAfterChange}
              />
            </HalfContainer>
          </SectionContainer>
          <InformationField />
          <SeparatorVertical $margin={36} />
          <SalesDetailField />
          <SeparatorVertical $margin={64} />
          <ProductDetailField />
          <SeparatorVertical $margin={36} />
          <CostField />
          <FormDivider />
        </div>
        <BottomSection />
      </FormContent>
    </Form>
  );
}
